import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useToast
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiFolder, 
  FiFolderPlus,
  FiRefreshCw,
  FiTrendingUp,
  FiBarChart
} from 'react-icons/fi';
import { ContentManagementService, type ContentCategory } from '../services/contentManagementService';
import { useAuth } from '../contexts/AuthContext';
import IconWrapper from './IconWrapper';

interface ContentCategorizationProps {
  onCategorySelect?: (categoryId: string) => void;
  showHeader?: boolean;
}

const ContentCategorization: React.FC<ContentCategorizationProps> = ({
  onCategorySelect,
  showHeader = true
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Form states
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [parentCategory, setParentCategory] = useState('');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const categoriesData = await ContentManagementService.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast({
        title: 'Error loading categories',
        description: 'Failed to load categories. Please try again.',
        status: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryName.trim()) {
      toast({
        title: 'Category name required',
        description: 'Please enter a category name.',
        status: 'error'
      });
      return;
    }

    try {
      setIsCreating(true);
      await ContentManagementService.createCategory(
        categoryName,
        categoryDescription,
        parentCategory || undefined
      );
      
      toast({
        title: 'Category created',
        description: 'Category has been created successfully.',
        status: 'success'
      });
      
      setCategoryName('');
      setCategoryDescription('');
      setParentCategory('');
      await loadCategories();
    } catch (error: any) {
      console.error('Error creating category:', error);
      toast({
        title: 'Error creating category',
        description: error.message || 'Failed to create category.',
        status: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditCategory = async (categoryId: string) => {
    try {
      await ContentManagementService.updateCategory(categoryId, {
        name: categoryName,
        description: categoryDescription,
        parentId: parentCategory || undefined
      });
      
      toast({
        title: 'Category updated',
        description: 'Category has been updated successfully.',
        status: 'success'
      });
      
      setIsEditing(null);
      setCategoryName('');
      setCategoryDescription('');
      setParentCategory('');
      await loadCategories();
    } catch (error: any) {
      console.error('Error updating category:', error);
      toast({
        title: 'Error updating category',
        description: error.message || 'Failed to update category.',
        status: 'error'
      });
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      await ContentManagementService.deleteCategory(selectedCategory);
      
      toast({
        title: 'Category deleted',
        description: 'Category has been deleted successfully.',
        status: 'success'
      });
      
      setIsDeleteModalOpen(false);
      setSelectedCategory(null);
      await loadCategories();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Error deleting category',
        description: error.message || 'Failed to delete category.',
        status: 'error'
      });
    }
  };

  const startEdit = (category: ContentCategory) => {
    setIsEditing(category.id);
    setCategoryName(category.name);
    setCategoryDescription(category.description);
    setParentCategory(category.parentId || '');
  };

  const cancelEdit = () => {
    setIsEditing(null);
    setCategoryName('');
    setCategoryDescription('');
    setParentCategory('');
  };

  const getParentCategoryName = (parentId: string): string => {
    const parent = categories.find(cat => cat.id === parentId);
    return parent ? parent.name : 'Unknown';
  };

  const getSubcategories = (categoryId: string): ContentCategory[] => {
    return categories.filter(cat => cat.parentId === categoryId);
  };

  const renderCategoryCard = (category: ContentCategory) => {
    const subcategories = getSubcategories(category.id);
    const isEditingThis = isEditing === category.id;

    return (
      <Box
        key={category.id}
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={6}
        transition="all 0.2s"
        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
      >
        <VStack align="stretch" gap={4}>
          <HStack justify="space-between">
            <HStack gap={3}>
              <IconWrapper icon={FiFolder} size={20} color="purple.400" />
              <VStack align="start" gap={1}>
                {isEditingThis ? (
                  <Input
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="Category name"
                    size="sm"
                  />
                ) : (
                  <Text fontWeight="bold" fontSize="lg" color="white">
                    {category.name}
                  </Text>
                )}
                {category.parentId && (
                  <Badge colorScheme="blue" fontSize="xs">
                    Subcategory of {getParentCategoryName(category.parentId)}
                  </Badge>
                )}
              </VStack>
            </HStack>
            
            <HStack gap={2}>
              <Tooltip label="Content count">
                <Badge colorScheme="purple" fontSize="sm">
                  {category.contentCount} items
                </Badge>
              </Tooltip>
              
              {user && (
                <HStack gap={1}>
                  <Tooltip label="Edit category">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<IconWrapper icon={FiEdit} size={14} />}
                      onClick={() => startEdit(category)}
                      aria-label="Edit category"
                    />
                  </Tooltip>
                  <Tooltip label="Delete category">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      icon={<IconWrapper icon={FiTrash2} size={14} />}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setIsDeleteModalOpen(true);
                      }}
                      aria-label="Delete category"
                    />
                  </Tooltip>
                </HStack>
              )}
            </HStack>
          </HStack>

          {isEditingThis ? (
            <VStack align="stretch" gap={3}>
              <Textarea
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                placeholder="Category description"
                size="sm"
                rows={3}
              />
              <Select
                value={parentCategory}
                onChange={(e) => setParentCategory(e.target.value)}
                placeholder="Select parent category (optional)"
                size="sm"
              >
                {categories
                  .filter(cat => cat.id !== category.id)
                  .map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </Select>
              <HStack gap={2}>
                <Button size="sm" colorScheme="purple" onClick={() => handleEditCategory(category.id)}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit}>
                  Cancel
                </Button>
              </HStack>
            </VStack>
          ) : (
            <>
              <Text color="gray.400" fontSize="sm">
                {category.description}
              </Text>
              
              {subcategories.length > 0 && (
                <VStack align="stretch" gap={2}>
                  <Text fontSize="sm" fontWeight="medium" color="white">
                    Subcategories:
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={2}>
                    {subcategories.map(subcat => (
                      <Box
                        key={subcat.id}
                        bg="gray.700"
                        p={3}
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ bg: 'gray.600' }}
                        onClick={() => onCategorySelect?.(subcat.id)}
                      >
                        <HStack justify="space-between">
                          <Text fontSize="sm" fontWeight="medium">
                            {subcat.name}
                          </Text>
                          <Badge colorScheme="blue" fontSize="xs">
                            {subcat.contentCount}
                          </Badge>
                        </HStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                </VStack>
              )}
            </>
          )}
        </VStack>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  return (
    <Container maxW="container.xl">
      <VStack gap={6} align="stretch">
        {showHeader && (
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiFolder} size={24} style={{ marginRight: '8px' }} />
              Content Categories
            </Heading>
            <Tooltip label="Refresh categories">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                onClick={loadCategories}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
        )}

        {/* Create Category Form */}
        {user && (
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <VStack align="stretch" gap={4}>
              <HStack>
                <IconWrapper icon={FiFolderPlus} size={20} color="purple.400" />
                <Text fontWeight="bold" color="white">
                  Create New Category
                </Text>
              </HStack>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <FormControl>
                  <FormLabel>Category Name</FormLabel>
                  <Input
                    placeholder="Enter category name"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Parent Category (Optional)</FormLabel>
                  <Select
                    value={parentCategory}
                    onChange={(e) => setParentCategory(e.target.value)}
                    placeholder="Select parent category"
                    bg="gray.700"
                    border="none"
                    color="white"
                    _focus={{ bg: 'gray.600' }}
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
              
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  placeholder="Enter category description"
                  value={categoryDescription}
                  onChange={(e) => setCategoryDescription(e.target.value)}
                  bg="gray.700"
                  border="none"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ bg: 'gray.600' }}
                  rows={3}
                />
              </FormControl>
              
              <Button
                colorScheme="purple"
                onClick={handleCreateCategory}
                isLoading={isCreating}
                loadingText="Creating..."
                leftIcon={<IconWrapper icon={FiPlus} size={16} />}
              >
                Create Category
              </Button>
            </VStack>
          </Box>
        )}

        {/* Categories List */}
        {categories.length === 0 ? (
          <Center py={8}>
            <VStack gap={2}>
              <Text color="gray.500">No categories found</Text>
              <Text fontSize="sm" color="gray.400">
                Create your first category to get started
              </Text>
            </VStack>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
            {categories
              .filter(cat => !cat.parentId) // Show only parent categories
              .map(renderCategoryCard)}
          </SimpleGrid>
        )}

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Delete Category</ModalHeader>
            <ModalBody>
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  Are you sure you want to delete this category? This action cannot be undone.
                  Any subcategories will also be affected.
                </AlertDescription>
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteCategory}>
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default ContentCategorization;
